import { ApolloError } from '@apollo/client'
import Groups from '@mui/icons-material/Groups'
import Person from '@mui/icons-material/Person'
import { Avatar, Box, Button, Stack, Typography, useTheme } from '@mui/material'
import { useFlag } from '@unleash/proxy-client-react'
import { useNavigate, useParams } from 'react-router-dom'
import { CoverImageType } from '../../../../../graphql/generated'
import { drawerVar } from '../../../../../providers/apollo/cache'
import logger from '../../../../../utils/logger'
import useCoverImage from '../../../hooks/useCoverImage'
import useUserProfileUpdate from '../../../hooks/useUserProfileUpdate'
import BucketMenuNew from './BucketMenuNew'

interface Props {
  bucket: any
  userId: string
}

const getBucketInitial = (title: string) => {
  return title?.charAt(0)?.toUpperCase() || '?'
}

const gradients = [
  'linear-gradient(154deg, #049f6e 15.29%, #73b255 84.04%)',
  'linear-gradient(154deg, #1C47CB -15.36%, #02E39F 105.31%)',
  'linear-gradient(153deg, #5B62FF 0.71%, #C985FF 93.55%)'
]

const getGradientForBucket = (bucketId: string) => {
  // Use bucket ID to consistently select the same gradient for each bucket
  const hash = bucketId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return gradients[hash % gradients.length]
}

export default function BucketItem({ bucket, userId }: Readonly<Props>) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { bucketId } = useParams<{ bucketId: string }>()
  const { mutation } = useUserProfileUpdate()
  const thumbnailsEnabled = useFlag('thumbnailsEnabled')

  // Get cover image ID from new coverImages field or fallback to deprecated field
  const coverImage = bucket?.coverImages?.find((img: any) => img?.coverImageType === CoverImageType.Cover)
  const coverImageId = coverImage?.id || bucket?.coverImageId

  // Fetch the actual cover image data
  const { data: coverImageData } = useCoverImage(bucket.id, coverImageId)

  const handleClick = () => {
    drawerVar(null)
    mutation({
      variables: { data: { currentBucketId: bucket.id } },
      onCompleted: () => {
        if (bucket.id === userId) {
          window.location.href = `/u/${userId}`
          return
        }
        navigate(`/bucket/${bucket.id}`)
      },
      onError: (error: ApolloError) => {
        logger.error(error)
        if (bucket.id === userId) {
          window.location.href = `/u/${userId}`
          return
        }
        navigate(`/bucket/${bucket.id}`)
      }
    })
  }

  // Create data URL from base64 if available
  const coverImageSrc = coverImageData?.data ? `data:${coverImageData.type || 'image/jpeg'};base64,${coverImageData.data}` : null

  return (
    <Stack
      key={bucket.id}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        flexDirection: 'row',
        border: bucketId === bucket.id ? `1px solid ${theme.palette.customViolet[600]}` : `1px solid transparent`,
        width: '100%',
        borderRadius: '8px',
        bgcolor: bucketId === bucket.id ? theme.palette.customViolet[50] : 'transparent',
        '&:hover': {
          bgcolor: theme.palette.customViolet[50],
          border: `1px solid ${theme.palette.customViolet[500]}`
        }
      }}
    >
      <Button
        onClick={handleClick}
        data-test={`bucket-switcher-${bucket.id}`}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          flex: 1,
          gap: '8px',
          minWidth: 0,
          padding: '4px 8px',
          transition: 'all .3s ease',
          color: theme.palette.secondary.main,
          '&:hover': {
            bgcolor: 'transparent'
          }
        }}
      >
        {thumbnailsEnabled && (
          <Avatar
            src={coverImageSrc || undefined}
            sx={{
              width: 54,
              height: 54,
              borderRadius: '6px',
              fontSize: '1.5rem',
              fontWeight: 800,
              background: coverImageSrc ? undefined : getGradientForBucket(bucket.id),
              bgcolor: coverImageSrc ? undefined : 'transparent',
              border: coverImageSrc ? undefined : '1px solid #BDC0FF',
              color: coverImageSrc ? theme.palette.text.primary : '#fff'
            }}
          >
            {getBucketInitial(bucket.title)}
          </Avatar>
        )}
        <Stack alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              fontWeight: bucketId === bucket.id ? 600 : 500,
              fontSize: '1.125rem',
              width: '100%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left'
            }}
          >
            {bucket.title}
          </Typography>
          {thumbnailsEnabled ? (
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
              {bucket.ownerId === userId && (
                <Box
                  sx={{
                    bgcolor: theme.palette.customViolet[600],
                    color: 'white',
                    borderRadius: '100px',
                    padding: '1px 8px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: 1.2
                  }}
                >
                  You
                </Box>
              )}
              <Typography sx={{ fontSize: '.75rem', fontWeight: 600, color: theme.palette.text.primary }}>
                {(bucket.bucketUserCount ?? 0) > 1 ? `${bucket.bucketUserCount} people` : '1 person'}
              </Typography>
            </Stack>
          ) : (
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: bucketId === bucket.id ? theme.palette.secondary.main : theme.palette.customGrey[500],
                  lineHeight: 1
                }}
              >
                {(bucket.bucketUserCount ?? 0) > 1 ? <Groups sx={{ fontSize: '1.25rem' }} /> : <Person sx={{ fontSize: '1.25rem' }} />}
              </Box>
              <Typography sx={{ fontSize: '.75rem', fontWeight: 500 }}>
                {(bucket.bucketUserCount ?? 0) > 1
                  ? `Shared with ${(bucket.bucketUserCount ?? 0) - 1} ${(bucket.bucketUserCount ?? 0) - 1 === 1 ? 'Person' : 'People'}`
                  : 'Personal'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Button>
      {bucketId === bucket.id && (
        <Box sx={{ paddingRight: '8px' }}>
          <BucketMenuNew />
        </Box>
      )}
    </Stack>
  )
}
